import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types/Property';
import './ProtectedRoute.css';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: UserRole[];
  fallback?: React.ReactNode;
  redirectTo?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles = [],
  fallback,
  redirectTo,
}) => {
  const { isAuthenticated, user, isLoading } = useAuth();

  // Mostrar loading enquanto verifica autenticação
  if (isLoading) {
    return (
      <div className="route-loading">
        <div className="loading-spinner"></div>
        <p>Verificando permissões...</p>
      </div>
    );
  }

  // Se não está autenticado
  if (!isAuthenticated || !user) {
    if (fallback) {
      return <>{fallback}</>;
    }
    
    return (
      <div className="route-unauthorized">
        <div className="unauthorized-content">
          <div className="unauthorized-icon">🔒</div>
          <h2>Acesso Restrito</h2>
          <p>Você precisa fazer login para acessar esta página.</p>
        </div>
      </div>
    );
  }

  // Se há roles específicas requeridas
  if (requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.includes(user.role as UserRole);
    
    if (!hasRequiredRole) {
      if (fallback) {
        return <>{fallback}</>;
      }
      
      return (
        <div className="route-unauthorized">
          <div className="unauthorized-content">
            <div className="unauthorized-icon">🚫</div>
            <h2>Acesso Negado</h2>
            <p>
              Você não tem permissão para acessar esta página.
              <br />
              <strong>Função necessária:</strong> {requiredRoles.join(' ou ')}
              <br />
              <strong>Sua função:</strong> {user.role}
            </p>
          </div>
        </div>
      );
    }
  }

  // Se chegou até aqui, tem permissão
  return <>{children}</>;
};

// Componente para verificar se o usuário tem uma role específica
interface RoleGuardProps {
  children: React.ReactNode;
  requiredRole: UserRole;
  fallback?: React.ReactNode;
}

export const RoleGuard: React.FC<RoleGuardProps> = ({
  children,
  requiredRole,
  fallback,
}) => {
  const { user } = useAuth();

  if (!user || user.role !== requiredRole) {
    return fallback ? <>{fallback}</> : null;
  }

  return <>{children}</>;
};

// Hook para verificar roles
export const useRole = () => {
  const { user } = useAuth();

  const hasRole = (role: UserRole) => {
    return user?.role === role;
  };

  const hasAnyRole = (roles: UserRole[]) => {
    return user && roles.includes(user.role as UserRole);
  };

  const isAdmin = () => {
    return hasRole(UserRole.ADMIN);
  };
  const isLocador = () => hasRole(UserRole.LOCADOR);
  const isLocatario = () => hasRole(UserRole.LOCATARIO);

  return {
    user,
    role: user?.role,
    hasRole,
    hasAnyRole,
    isAdmin,
    isLocador,
    isLocatario,
  };
};
